import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { AccountStationInterFace } from '../../../interface/account-locations-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableAccountStationFilterApplied,
  fetchAccountStation,
  setSortData,
} from '../../../redux/slices/AccountLocationSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import StationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';

interface StationsProperties {
  onClose: () => void;
}

/**
 * Stations table component
 * @param {StationsProperties} param0 - The component props.
 * @param {() => void} param0.onClose - Callback invoked when the panel should close.
 * @returns {React.JSX.Element} The rendered stations table.
 */
function Stations({ onClose }: StationsProperties): React.JSX.Element {
  const { t, i18n } = useTranslation(['account', 'userRole']);
  const [searchParameters] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const accountCustomId = id || searchParameters.get('accountCustomId');
  const { accountStation, limit, page, loading, totalPages, totalResults, sortBy, sortField } =
    useAppSelector((state) => state.accountLocation);
  const dispatch = useAppDispatch();
  const previousAccountCustomId = useRef<string | null>(null);
  const devicesReference = useRef<HTMLDivElement | null>(null);
  const [selectedStation, setSelectedStation] = useState<AccountStationInterFace | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const column = [
    {
      label: t('station'),
      accessorKey: 'name' as keyof AccountStationInterFace,
      sortAllow: true,
      /**
       * Side panel component to show on clicking the cell value
       * @param {any} cachestation - The cachestation
       */
      customData: (cachestation: AccountStationInterFace) => (
        <button
          type="button"
          className="accounts-management"
          onClick={() => {
            setSelectedStation(cachestation);
            setIsPanelOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setSelectedStation(cachestation);
              setIsPanelOpen(true);
            }
          }}>
          {cachestation.name}
        </button>
      ),
    },
    {
      label: t('location'),
      accessorKey: 'address' as keyof AccountStationInterFace,
      sortAllow: true,
    },
    {
      label: t('noOfDevice'),
      accessorKey: 'totalDevices' as keyof AccountStationInterFace,
      sortAllow: true,
      /**
       * Side panel component to show on clicking the cell value
       * @param {any} station - The station
       */
      customData: (station: AccountStationInterFace) => <span>{station?.totalDevices ?? 0}</span>,
    },
  ];

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: any) => {
    if (!loading && accountCustomId) {
      dispatch(fetchAccountStation({ accountCustomId, ...data }));
    }
  };
  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    if (value.sortField && value.sortBy) {
      dispatch(setSortData({ sortField: value.sortField, sortBy: value.sortBy as 'asc' | 'desc' }));
    }
    fetchData({ sortBy, sortField, ...value });
  };  

  /**
   * Handle sort change function
   * @param {any} value - The value
   */
  const handleSortChange = (value: any) => {
    if (value.sortField && value.sortBy) {
      dispatch(setSortData({ sortField: value.sortField, sortBy: value.sortBy as 'asc' | 'desc' }));
    }
    fetchData({ page: 1, limit, ...value });
  };

  useEffect(() => {
    if (
      accountStation.length === 0 ||
      (accountCustomId && previousAccountCustomId.current !== accountCustomId)
    ) {
      fetchData({
        page,
        limit,
        hardRefresh: true,
      });
      dispatch(disableAccountStationFilterApplied());
      previousAccountCustomId.current = accountCustomId;
    }
  }, [accountCustomId]);
  useEffect(() => {
    /**
     * Handle click outside the component to trigger onClose
     * @param {any} event - The event
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (devicesReference.current && !devicesReference.current.contains(event.target as Node)) {
        if (isPanelOpen) {
          setIsPanelOpen(false);
          setSelectedStation(null);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div ref={devicesReference} dir={i18n.dir()}>
      <PageTitle
        title={t('stations')}
        buttonSvgIcon={<CloseIcon />}
        buttonType="none"
        onClickButton={() => {
          if (isPanelOpen) {
            setIsPanelOpen(false);
            setSelectedStation(null);
          } else {
            onClose();
          }
        }}
        buttonLabel=" "
      />

      {isPanelOpen && selectedStation ? (
        <StationSidePanel fetchIdFrom="props" id={selectedStation._id} />
      ) : (
        <CustomTable
          header={column}
          sortData={{ sortBy, sortField }}
          serialNo
          limit={limit}
          totalPages={totalPages}
          data={accountStation}
          page={page}
          handleSortChange={handleSortChange}
          loading={loading}
          totalResults={totalResults}
          pageLimitChange={pageNateLimitChange}
          height={370}
        />
      )}
    </div>
  );
}

export default Stations;
